import { useEffect, useRef, useCallback } from 'react';

interface UseInfiniteScrollOptions {
  /** Function to call when more items should be loaded */
  onLoadMore: () => void | Promise<void>;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether currently loading */
  isLoading: boolean;
  /** Root margin for intersection observer (default: 400px - triggers before reaching bottom) */
  rootMargin?: string;
  /** Threshold for intersection (default: 0) */
  threshold?: number;
  /** Whether infinite scroll is enabled (default: true) */
  enabled?: boolean;
}

/**
 * Hook for implementing infinite scroll using IntersectionObserver.
 * Returns a ref to attach to a sentinel element at the bottom of your list.
 *
 * @example
 * ```tsx
 * const { sentinelRef } = useInfiniteScroll({
 *   onLoadMore: loadMore,
 *   hasMore,
 *   isLoading: loading,
 * });
 *
 * return (
 *   <div>
 *     {items.map(item => <Item key={item.id} />)}
 *     <div ref={sentinelRef} />
 *   </div>
 * );
 * ```
 */
export function useInfiniteScroll({
  onLoadMore,
  hasMore,
  isLoading,
  rootMargin = '400px',
  threshold = 0,
  enabled = true,
}: UseInfiniteScrollOptions) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef(isLoading);
  const hasMoreRef = useRef(hasMore);

  // Keep refs in sync with props
  useEffect(() => {
    loadingRef.current = isLoading;
  }, [isLoading]);

  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);

  const handleIntersect = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMoreRef.current && !loadingRef.current) {
        onLoadMore();
      }
    },
    [onLoadMore]
  );

  useEffect(() => {
    if (!enabled) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(handleIntersect, {
      root: null, // viewport
      rootMargin,
      threshold,
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [handleIntersect, rootMargin, threshold, enabled]);

  return { sentinelRef };
}

export default useInfiniteScroll;
